import { atom, useAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useBeforeunload } from 'react-beforeunload'
import { Extension, ParkingLot, useSettingsState } from '~/atoms/SettingsState'
import { useSipInstance } from '~/atoms/SipInstanceState'
import { useUserState } from '~/atoms/UserState'
import {
  ConferenceStateResponse,
  PhoneStatus,
  TrackedExtension,
  TrackedParkingLot,
  useWebphoneState,
} from '~/atoms/WebphoneState'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useAppVersion } from '~/hooks/useAppVersion'
import { useOnLogout } from '~/hooks/useOnLogout'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { useRecentCalls } from '~/pages/WebPhone/atoms/RecentCalls.atom'
import {
  PARKING_LOT_PREFIX_DIGITS,
  PARKING_LOT_PREFIX_STRING,
  PRESENCE_SUBSCRIPTION_REFRESH_INTERVAL,
} from '~/pages/WebPhone/utils/constants'
import { useGetSipConnectionInfo } from '~/queries/sip'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { SpeedDialState } from '~/types/WebphoneTypes'
import { Analytics } from '~/utils/analytics'
import { isElectron } from '~/utils/common'
import {
  PresenceStateChange,
  SessionStateChange,
  SipClient,
} from '~/utils/sipclient'
import { PresenceState } from '~/utils/xml'

import { toMaskedPhoneNumber } from '@mangovoice/core'
import { RegistererState, SessionState } from '@mangovoice/sip'
import { ExtensionPreview, PhoneCall, PhoneCallStatus } from '@mangovoice/types'

import { WebphoneOperation } from './toast/eventHandlers/webphoneToastEventHandler'
import useCloudwatchLogger from './useCloudwatchLogger'
import { useEmitterService } from './useEmitterService'

const MAX_CALLS = 2
const CLIENT_AUDIO_STREAM_KEY = 'client'

const needsAudioConnectedAtom = atom<boolean>(false)
const needsClientAudioInputAtom = atom<boolean>(false)
const connectionRefreshRequestedAtom = atom<boolean>(false)
const registrationStatusAtom = atom<RegistererState>(RegistererState.Initial)
const registrationErrorAtom = atom<Error | null>(null)
const isPhoneCall = (value: unknown): value is PhoneCall => {
  return (
    value !== null &&
    typeof value === 'object' &&
    'callUuid' in value &&
    typeof (value as PhoneCall).callUuid === 'string'
  )
}

type CallAudio = {
  stream: MediaStream
  audioElement?: HTMLAudioElement
}

const callAudioStreamsAtom = atom<Map<string, CallAudio>>(
  new Map<string, CallAudio>(),
)

export enum TransportConnectionState {
  INITIAL = 'INITIAL',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
}
const transportConnectionStateAtom = atom<TransportConnectionState>(
  TransportConnectionState.INITIAL,
)

/**
 * Hub for all phone endpoints in the app.
 * Only one should be live at a time.
 * @returns
 */
export const usePhoneHub = (audioElementId: string) => {
  const appVersion = useAppVersion()

  const [initialSetup, setInitialSetup] = useState(true)
  const [transportReconnect, setTransportReconnect] = useState(false)
  const [needsReregister, setNeedsReregister] = useState(false)
  const [currentAudioInput, setCurrentAudioInput] = useState<string>('')
  const [currentAudioOutput, setCurrentAudioOutput] = useState<string>('')

  const audioContext = useRef<AudioContext | undefined>(undefined)
  const [callAudioStreams, setCallAudioStreams] = useAtom(callAudioStreamsAtom)

  const [needsClientAudioInput, setNeedsClientAudioInput] = useAtom(
    needsClientAudioInputAtom,
  )
  const [fetchingAudioInput, setFetchingAudioInput] = useState(false)
  const [needsAudioCleanupCheck, setNeedsAudioCleanupCheck] = useState(false)

  const [needsAudioConnected, setNeedsAudioConnected] = useAtom(
    needsAudioConnectedAtom,
  )
  const [connectionRefreshRequested, setConnectionRefreshRequested] = useAtom(
    connectionRefreshRequestedAtom,
  )
  const [registrationState, setRegistrationState] = useAtom(
    registrationStatusAtom,
  )
  const [, setRegistrationError] = useAtom(registrationErrorAtom)
  const [transportConnectionState, setTransportConnectionState] = useAtom(
    transportConnectionStateAtom,
  )

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const { logger } = useCloudwatchLogger('Webphone')
  const { logger: sipLogger } = useCloudwatchLogger('SipClient')

  const {
    settings: {
      webPhone: { allowClickToCallIncoming },
    },
  } = useSettingsState()

  const { currentUserExtensionId: userExtensionId, pbxId } = useUserState()
  const { data: sipConn, refetch: requerySipConnectionInfo } =
    useGetSipConnectionInfo({ extensionId: userExtensionId ?? 0 })

  const {
    triggerReregister,
    sipInstance: [getSipInstance, setSipInstance],
  } = useSipInstance()

  const {
    phoneStatus,
    phoneCalls: {
      active: { activeCall, activeCallUuid, setActiveCallUuid },
      list: { callList, upsertCall, removeCall },
      conference: { conferencedCalls, isMuted: conferenceIsMuted },
      invite: {
        pendingInvitation,
        pendingInvitationUuid,
        setPendingInvitationUuid,
      },
    },
    trackedParkingLots,
    updateTrackedParkingLot,
    trackedExtensions,
    updateTrackedExtension,
    audio: {
      output: {
        preference: [selectedOutputDeviceId],
        setAudioOutputStream,
      },
      input: {
        preference: [selectedInputDeviceId],
        getAudioInputStream,
      },
    },
  } = useWebphoneState({
    pbxId: pbxId ?? 0,
    registrationState,
    transportConnectionState,
  })

  const hardResetSipClient = () => {
    void requerySipConnectionInfo()
    triggerReregister(setNeedsReregister)
  }

  if (connectionRefreshRequested) {
    hardResetSipClient()
    setConnectionRefreshRequested(false)
  }

  // If the SIP connection info changes, trigger a re-registration
  useEffect(() => {
    triggerReregister(setNeedsReregister)
  }, [sipConn])

  if (
    !transportReconnect &&
    phoneStatus === PhoneStatus.TRANSPORT_DISCONNECTED
  ) {
    hardResetSipClient()
    setTransportReconnect(true)
  }
  if (transportReconnect && phoneStatus === PhoneStatus.READY) {
    setTransportReconnect(false)
  }

  const getCallerInfo = useCallback(
    (callUuid: string): { name: string | null; number: string } => {
      const sipClient = getSipInstance()
      if (!sipClient) {
        logger?.info(
          `No SIP client available for remote caller info | ${callUuid}`,
        )
        return { name: null, number: 'Unknown Number' }
      }
      try {
        const { displayName: name, user: number } =
          sipClient.getRemoteCallerId(callUuid)
        return { name, number: number ?? 'Unknown Number' }
      } catch {
        logger?.info(`Failed to find remote caller info for ${callUuid}`)
        return { name: null, number: 'Unknown Number' }
      }
    },
    [getSipInstance],
  )

  // TODO: Wrap the audio stream atom management in a separate hook
  const upsertCallAudioStream = (
    callUuid: string,
    stream: MediaStream,
    audioElement?: HTMLAudioElement,
  ) => {
    setCallAudioStreams((list) => {
      const newStreams = new Map(list)
      newStreams.set(callUuid, { stream, audioElement })
      return newStreams
    })
  }
  const removeCallAudioStream = (callUuid: string) => {
    setCallAudioStreams((list) => {
      const newStreams = new Map(list)
      newStreams.delete(callUuid)
      return newStreams
    })
  }
  const clearCallAudioStreams = () => [
    callAudioStreams.forEach((callAudio) => {
      callAudio.stream.getTracks().forEach((t) => t.stop())
      if (callAudio.audioElement) {
        callAudio.audioElement.srcObject = null
        callAudio.audioElement.remove()
      }
    }),
    setCallAudioStreams(new Map<string, CallAudio>()),
  ]
  const getCallAudioStream = useCallback(
    (callUuid: string) => {
      return callAudioStreams.get(callUuid)
    },
    [callAudioStreams],
  )

  if (needsClientAudioInput) {
    if (!getCallAudioStream(CLIENT_AUDIO_STREAM_KEY)) {
      const sipClient = getSipInstance()
      if (sipClient && !fetchingAudioInput) {
        setFetchingAudioInput(true)
        getAudioInputStream()
          .then((stream) => {
            upsertCallAudioStream(CLIENT_AUDIO_STREAM_KEY, stream)
            setNeedsClientAudioInput(false)
          })
          .finally(() => {
            setFetchingAudioInput(false)
          })
      }
    }
  }

  if (needsAudioCleanupCheck) {
    if (callList.size === 0) {
      try {
        audioContext.current?.close().then(() => {
          audioContext.current = undefined
        })
      } catch (e) {
        logger?.info('Error closing audio context, likely already closed')
      }
      clearCallAudioStreams()
    }
    setNeedsAudioCleanupCheck(false)
  }

  const updateCallRemoteStream = (callUuid: string) => {
    const sipClient = getSipInstance()
    if (!sipClient) return
    const stream = sipClient.getRemoteStream(callUuid)
    const audio = new Audio()
    audio.srcObject = stream
    audio.muted = true
    upsertCallAudioStream(callUuid, stream, audio)
  }

  const connectAudio = useCallback(
    async (sipClient: SipClient, callUuids: string[], isMuted: boolean) => {
      if (callUuids.length === 0) return
      if (audioContext) {
        try {
          await audioContext.current?.close()
        } catch (e) {
          logger?.info('Error closing audio context, likely already closed')
        }
      }
      audioContext.current = new AudioContext()

      const clientInputStream = getCallAudioStream(CLIENT_AUDIO_STREAM_KEY)
      if (!clientInputStream) {
        logger?.info('Client audio stream not found')
        throw new Error('Client audio stream not found')
      }
      clientInputStream.stream.getTracks()[0].enabled = !isMuted
      const clientSource = audioContext.current.createMediaStreamSource(
        clientInputStream.stream,
      )

      if (callUuids.length === 1) {
        const callAudio = getCallAudioStream(callUuids[0])
        if (!callAudio) {
          logger?.info('Call audio stream not found')
          throw new Error('Call audio stream not found')
        }
        const callSource = audioContext.current.createMediaStreamSource(
          callAudio.stream,
        )

        const callGain = audioContext.current.createGain()
        callGain.gain.value = 1
        callSource.connect(callGain)
        const clientDestination =
          audioContext.current.createMediaStreamDestination()
        callGain.connect(clientDestination)

        const clientGain = audioContext.current.createGain()
        clientGain.gain.value = 1
        clientSource.connect(clientGain)
        const callDestination =
          audioContext.current.createMediaStreamDestination()
        clientGain.connect(callDestination)

        setAudioOutputStream(clientDestination.stream)
        sipClient.setRemoteStream(callUuids[0], callDestination.stream)
      } else {
        const callKeys = [CLIENT_AUDIO_STREAM_KEY, ...callUuids]
        const callAudioSources = new Map<string, MediaStreamAudioSourceNode>()
        callAudioSources.set(CLIENT_AUDIO_STREAM_KEY, clientSource)
        callUuids.forEach((callKey) => {
          if (!audioContext.current) {
            logger?.info('Audio context not found')
            throw new Error('Audio context not found')
          }
          const callAudio = getCallAudioStream(callKey)
          if (!callAudio) {
            logger?.info('Call audio stream not found')
            throw new Error('Call audio stream not found')
          }
          const callSource = audioContext.current.createMediaStreamSource(
            callAudio.stream.clone(),
          )
          callAudioSources.set(callKey, callSource)
        })

        const callAudioDestinations = new Map<
          string,
          MediaStreamAudioDestinationNode
        >()
        callKeys.forEach((callKey) => {
          if (!audioContext.current) {
            logger?.info('Audio context not found')
            throw new Error('Audio context not found')
          }
          const callDestination =
            audioContext.current.createMediaStreamDestination()
          const gainNode = audioContext.current.createGain()
          callAudioSources.forEach((source, key) => {
            if (key !== callKey) {
              source.connect(gainNode)
            }
          })
          gainNode.gain.value = 1
          gainNode.connect(callDestination)
          callAudioDestinations.set(callKey, callDestination)
        })

        const clientStream = callAudioDestinations.get(
          CLIENT_AUDIO_STREAM_KEY,
        )?.stream
        if (!clientStream) {
          logger?.info('Client audio stream not found')
          throw new Error('Client audio stream not found')
        }
        setAudioOutputStream(clientStream)
        callUuids.forEach((callUuid) => {
          const callStream = callAudioDestinations.get(callUuid)?.stream
          if (!callStream) {
            logger?.info('Call audio stream not found')
            throw new Error('Call audio stream not found')
          }
          sipClient.setRemoteStream(callUuid, callStream)
        })
      }
    },
    [getCallAudioStream, setAudioOutputStream, audioContext],
  )

  // #region SIP event handlers
  const handleSipClientRegistrationChange = useCallback(
    (state: RegistererState) => {
      setRegistrationState(state)
      logger?.info(`onRegistrationStateChanged: ${state}`)
    },
    [setRegistrationState],
  )

  const handleOutboundSessionStateChange = useCallback(
    (stateChange: SessionStateChange) => {
      logger?.info(
        `onSessionStateChange: ${stateChange.callUuid} ${stateChange.state}`,
      )
      const call = callList.get(stateChange.callUuid)
      if (!call) {
        logger?.info(`Call not found | ${stateChange.callUuid}`)
        return
      }
      switch (stateChange.state) {
        case SessionState.Establishing:
          upsertCall({
            ...call,
            status: PhoneCallStatus.RINGING,
          })
          break
        case SessionState.Established:
          upsertCall({
            ...call,
            startTime: new Date(),
            status: PhoneCallStatus.CONNECTED,
          })
          updateCallRemoteStream(stateChange.callUuid)
          if (stateChange.callUuid === activeCallUuid) {
            setNeedsAudioConnected(true)
          }
          break
        case SessionState.Terminating:
          upsertCall({
            ...call,
            status: PhoneCallStatus.ENDING_CALL,
          })
          break
        case SessionState.Terminated:
          upsertCall({ ...call, status: PhoneCallStatus.ENDED })
          removeCall(stateChange.callUuid)
          removeCallAudioStream(stateChange.callUuid)

          if (stateChange.callUuid === activeCallUuid) {
            // Check if we have any other calls that should become active
            const remainingCalls = new Map(callList)
            remainingCalls.delete(stateChange.callUuid)

            if (remainingCalls.size > 0) {
              const firstCallUuid = remainingCalls.keys().next().value

              if (firstCallUuid) {
                logger?.info(
                  'Found remaining calls after active call ended, selecting first one as active',
                )
                setActiveCallUuid(firstCallUuid)
                setNeedsAudioConnected(true)
              } else {
                setActiveCallUuid(null)
              }
            } else {
              setActiveCallUuid(null)
            }
          }

          if (stateChange.callUuid === pendingInvitationUuid) {
            setPendingInvitationUuid(null)
          }
          setNeedsAudioCleanupCheck(true)
          break
        default:
          break
      }
    },
    [activeCallUuid, callList, getCallerInfo],
  )

  const handleInboundSessionStateChange = useCallback(
    (stateChange: SessionStateChange) => {
      logger?.info(
        `onSessionStateChange: ${stateChange.callUuid} ${stateChange.state}`,
      )
      const call = callList.get(stateChange.callUuid)
      if (!call) {
        logger?.info(`Call not found | ${stateChange.callUuid}`)
        return
      }
      const { name, number } = getCallerInfo(stateChange.callUuid)
      switch (stateChange.state) {
        case SessionState.Establishing:
          upsertCall({
            ...call,
            number: number,
            name: name,
            status: PhoneCallStatus.RINGING,
          })
          break
        case SessionState.Established:
          upsertCall({
            ...call,
            startTime: new Date(),
            status: PhoneCallStatus.CONNECTED,
          })
          updateCallRemoteStream(stateChange.callUuid)
          if (stateChange.callUuid === activeCallUuid) {
            setNeedsAudioConnected(true)
          }
          break
        case SessionState.Terminating:
          upsertCall({
            ...call,
            status: PhoneCallStatus.ENDING_CALL,
          })
          break
        case SessionState.Terminated:
          upsertCall({ ...call, status: PhoneCallStatus.ENDED })
          removeCall(stateChange.callUuid)
          removeCallAudioStream(stateChange.callUuid)
          if (stateChange.callUuid === activeCallUuid) {
            setActiveCallUuid(null)
          }
          if (stateChange.callUuid === pendingInvitationUuid) {
            setPendingInvitationUuid(null)
          }
          setNeedsAudioCleanupCheck(true)
          break
        default:
          break
      }
    },
    [activeCallUuid, callList, getCallerInfo],
  )
  const handleIncomingInviteReceived = useCallback(
    ({
      callUuid,
      number,
      name,
      isClickToCall,
    }: {
      callUuid: string
      number: string
      name: string
      isClickToCall: boolean
    }) => {
      logger?.info('Incoming Invite Received')
      if (isClickToCall && !allowClickToCallIncoming) {
        const sipClient = getSipInstance()
        if (sipClient) sipClient.rejectCall(callUuid)
        logger?.info(`Invite auto-rejected. Click-to-call.`)
        return
      }
      if (pendingInvitation) {
        const sipClient = getSipInstance()
        if (sipClient) sipClient.rejectCall(callUuid)
        logger?.info(`Invite auto-rejected. Already managing an Invite.`)
        track(WebPhoneAnalyticsEvents.InviteRejectedSecondInvite)
        return
      }

      upsertCall({
        status: PhoneCallStatus.INITIAL,
        callUuid: callUuid,
        number: number,
        name: name,
        startTime: null,
        isOnHold: false,
        isMuted: false,
      })
      setPendingInvitationUuid(callUuid)
    },
    [pendingInvitation, allowClickToCallIncoming, getSipInstance],
  )
  const handleTransportConnected = useCallback(() => {
    setTransportConnectionState(TransportConnectionState.CONNECTED)
  }, [])
  const handleTransportDisconnected = useCallback(
    (error?: Error | undefined) => {
      if (error) {
        setTransportConnectionState(TransportConnectionState.DISCONNECTED)
        setRegistrationError(error)
      }
    },
    [],
  )
  const handleRegistrationRejected = useCallback(
    (errorMessage: string | undefined) => {
      logger?.info(`Registration rejected: ${errorMessage}`)
      setRegistrationError(new Error(errorMessage))
    },
    [],
  )
  const handleParkingSubscriptionUpdate = useCallback(
    ({ subscribedNumber, state }: PresenceStateChange) => {
      const matchingLot = trackedParkingLots.list.find(
        (lot) => lot.targetNumber === subscribedNumber,
      )
      if (matchingLot) {
        updateTrackedParkingLot({ id: matchingLot.id, presenceState: state })
      }
    },
    [trackedParkingLots.list],
  )
  const handleExtensionSubscriptionUpdate = useCallback(
    ({ subscribedNumber, state }: PresenceStateChange) => {
      const matchingExtension = trackedExtensions.list.find(
        (ext) => ext.uuid === subscribedNumber,
      )
      if (matchingExtension) {
        updateTrackedExtension({
          id: matchingExtension.id,
          presenceState: state,
        })
      }
    },
    [trackedExtensions.list],
  )
  // #endregion

  // Register SIP client and attach registration listeners
  // Depends on changes to SIP connection info and flags to re-register
  useEffect(() => {
    if (!sipConn) return
    if (initialSetup || needsReregister) {
      if (initialSetup) logger?.info('Initial SIP registration')
      if (needsReregister) logger?.info('SIP re-registration')
      const newSipClient = new SipClient({
        sipConnectionInfo: sipConn,
        logger: sipLogger,
        analytics: new Analytics(AnalyticsNamespace.SIP_CLIENT),
        userAgentString: `${isElectron ? 'WebPhone(DesktopApp)' : 'WebPhone'}/${appVersion}`,
      })
      newSipClient.on(
        'RegistrationStateChanged',
        handleSipClientRegistrationChange,
      )
      newSipClient.on('TransportConnected', handleTransportConnected)
      newSipClient.on('TransportDisconnected', handleTransportDisconnected)
      newSipClient.on('RegistrationRejected', handleRegistrationRejected)

      setSipInstance(newSipClient)
      if (initialSetup) setInitialSetup(false)
      if (needsReregister) {
        //Note: needsReregister won't update without this setTimeout. Possibly due to setting to true and false in same render cycle.
        setTimeout(() => {
          setNeedsReregister(false)
        }, 0)
      }
    }

    return () => {
      const sipClient = getSipInstance()
      if (sipClient) {
        logger?.debug('SIP de-registration')
        sipClient.off(
          'RegistrationStateChanged',
          handleSipClientRegistrationChange,
        )
        sipClient.off('TransportConnected', handleTransportConnected)
        sipClient.off('TransportDisconnected', handleTransportDisconnected)
        sipClient.off('RegistrationRejected', handleRegistrationRejected)

        sipClient.dispose()
        logger?.info('SIP de-registration success')
      }
    }
  }, [sipConn, initialSetup, needsReregister])
  // Attach SIP call event listeners
  // Depends on changes to the callbacks
  useEffect(() => {
    const sipClient = getSipInstance()
    if (!sipClient) return
    sipClient.on('OutboundSessionStateChange', handleOutboundSessionStateChange)
    sipClient.on('InboundSessionStateChange', handleInboundSessionStateChange)
    sipClient.on('IncomingInviteReceived', handleIncomingInviteReceived)
    sipClient.on('ParkingStateChanged', handleParkingSubscriptionUpdate)
    sipClient.on('ExtensionStateChanged', handleExtensionSubscriptionUpdate)

    return () => {
      sipClient.off(
        'OutboundSessionStateChange',
        handleOutboundSessionStateChange,
      )
      sipClient.off(
        'InboundSessionStateChange',
        handleInboundSessionStateChange,
      )
      sipClient.off('IncomingInviteReceived', handleIncomingInviteReceived)
      sipClient.off('ParkingStateChanged', handleParkingSubscriptionUpdate)
      sipClient.off('ExtensionStateChanged', handleExtensionSubscriptionUpdate)
    }
  }, [
    handleOutboundSessionStateChange,
    handleInboundSessionStateChange,
    handleIncomingInviteReceived,
    handleParkingSubscriptionUpdate,
    handleExtensionSubscriptionUpdate,
  ])

  useBeforeunload((event) => {
    const sipClient = getSipInstance()
    if (callList.size > 0) {
      event.preventDefault()
      if (!event.defaultPrevented) {
        sipClient?.dispose()
      }
    } else {
      sipClient?.dispose()
    }
  })

  if (currentAudioInput !== selectedInputDeviceId) {
    if (activeCall || conferencedCalls) {
      setNeedsAudioConnected(true)
    }
    setCurrentAudioInput(selectedInputDeviceId)
  }
  if (currentAudioOutput !== selectedOutputDeviceId) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const audioElement = document.getElementById(audioElementId) as any
    if (audioElement) {
      // Firefox should support this in release 116 https://github.com/mdn/content/issues/27748
      // With Firefox support, Typescript can include this in the HTMLMediaElement type.
      if ('setSinkId' in HTMLAudioElement.prototype) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        audioElement.setSinkId(selectedOutputDeviceId)
      }
      setCurrentAudioOutput(selectedOutputDeviceId)
    }
  }

  if (needsAudioConnected) {
    const sipClient = getSipInstance()
    const clientAudioStream = getCallAudioStream(CLIENT_AUDIO_STREAM_KEY)
    if (sipClient && activeCall && clientAudioStream) {
      if (
        conferencedCalls &&
        conferencedCalls
          .map((call) => getCallAudioStream(call.callUuid))
          .every(Boolean)
      ) {
        const callUuids = conferencedCalls.map((call) => call.callUuid)
        connectAudio(sipClient, callUuids, conferenceIsMuted).then(() => {
          setNeedsAudioConnected(false)
        })
      } else if (getCallAudioStream(activeCall.callUuid)) {
        connectAudio(sipClient, [activeCall.callUuid], activeCall.isMuted).then(
          () => {
            setNeedsAudioConnected(false)
          },
        )
      }
    }
  }

  const updatePresenceSubscriptions = (
    parkingLots: ParkingLot[],
    extensions: Extension[],
  ) => {
    const sipClient = getSipInstance()
    if (!sipClient) return
    parkingLots.forEach((lot) => {
      sipClient.subscribeToParking(lot.targetNumber)
    })
    extensions.forEach((ext) => {
      sipClient.subscribeToExtension(ext.uuid)
    })
  }

  const lotsToUpdate = trackedParkingLots.withPresence.filter(
    (lot) =>
      lot.presence === undefined || lot.presence === PresenceState.Unknown,
  )
  const extensionsToUpdate = trackedExtensions.withPresence.filter(
    (ext) =>
      ext.presence === undefined || ext.presence === PresenceState.Unknown,
  )
  if (
    registrationState === RegistererState.Registered &&
    transportConnectionState === TransportConnectionState.CONNECTED &&
    (lotsToUpdate.length > 0 || extensionsToUpdate.length > 0)
  ) {
    updatePresenceSubscriptions(lotsToUpdate, extensionsToUpdate)
  }

  // Set up resubscribe interval for presence updates, every 120 seconds
  // Depends on changes to the tracked parking lots and extensions
  useEffect(() => {
    const interval = setInterval(() => {
      updatePresenceSubscriptions(
        trackedParkingLots.list,
        trackedExtensions.list,
      )
    }, PRESENCE_SUBSCRIPTION_REFRESH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [trackedParkingLots.list, trackedExtensions.list, getSipInstance])
}

export interface PhoneEndpoint {
  state: {
    phoneStatus: PhoneStatus
    phoneCalls: {
      active: { activeCall: PhoneCall | undefined }
      list: { callList: Map<string, PhoneCall> }
      conference: ConferenceStateResponse
      invite: {
        pendingInvitation: PhoneCall | undefined
        pendingInvitationUuid: string | null
      }
      isAtMaxCallLimit: boolean
    }
    extensions: ExtensionPreview[]
    trackedExtensions: TrackedExtension[]
    trackedParkingLots: TrackedParkingLot[]
    registrationError: Error | null
    audioDevices: {
      output: {
        devices: MediaDeviceInfo[]
        preference: string
      }
      input: {
        devices: MediaDeviceInfo[]
        preference: string
      }
    }
    speedDial: SpeedDialState
  }
  actions: {
    switchCall: (callUuid: string) => Promise<void>
    startCall: (
      destination:
        | string
        | { contactNumber: ContactNumber; contact: ContactDetails },
      outboundCallerIdName?: string,
    ) => void
    endCall: (callUuid: string) => void
    answerCall: () => void
    answerCallWithHoldCurrent: () => void
    answerCallWithEndCurrent: () => void
    answerCallWithParkCurrent: (parkTarget: string) => void
    rejectCall: () => void
    rejectCallWithBusy: () => void
    /**
     * Toggle the mute state of a call
     * @param callUuid: The UUID of the call to mute
     * @param muted: undefined to toggle, true to mute, false to unmute
     */
    muteCall: (callUuid: string, muted?: boolean) => void
    /**
     * Toggle the hold state of a call
     * @param callUuid: The UUID of the call to hold
     * @param hold: undefined to toggle, true to hold, false to unhold
     */
    holdCall: (callUuid: string, hold?: boolean) => Promise<void>
    attendedTransferCall: (callUuid: string, targetCallUuid: string) => void
    transferCall: (callUuid: string, target: string) => void
    parkCall: (callUuid: string, parkTarget: string) => void
    unparkCall: (parkId: string) => void
    mergeCalls: (callUuids: string[]) => void
    splitCalls: () => void
    endConferenceCall: () => void
    toggleMuteConference: () => void
    toggleHoldConference: () => Promise<void>
    sendDtmf: (callUuid: string, digit: string) => void
    resetConnection: () => void
    switchAudioOutput: (deviceId: string) => void
    switchAudioInput: (deviceId: string) => void
  }
}

export interface UsePhoneEndpointParams {
  onCallEnd?: () => void
  onCallStart?: () => void
  onFinalCallEnd?: () => void
}

export const usePhoneEndpoint = ({
  onCallEnd,
  onCallStart,
  onFinalCallEnd,
}: UsePhoneEndpointParams): PhoneEndpoint => {
  const [, setNeedsAudioConnected] = useAtom(needsAudioConnectedAtom)
  const [, setNeedsClientAudioInput] = useAtom(needsClientAudioInputAtom)
  const [, setConnectionRefreshRequested] = useAtom(
    connectionRefreshRequestedAtom,
  )
  const [registrationState] = useAtom(registrationStatusAtom)
  const [registrationError] = useAtom(registrationErrorAtom)
  const [transportConnectionState] = useAtom(transportConnectionStateAtom)

  const [needsFinalCallEnd, setNeedsFinalCallEnd] = useState(false)

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const { emit } = useEmitterService()
  const { logger } = useCloudwatchLogger('Webphone')

  const { pbxId } = useUserState()

  const {
    sipInstance: [getSipInstance],
  } = useSipInstance()
  const sipClient = getSipInstance()

  useOnLogout(() => {
    if (sipClient?.isConnected) {
      sipClient?.dispose()
    }
  }, 'usePhoneEndpoint')

  const {
    phoneStatus,
    phoneCalls: {
      active: { activeCall, setActiveCallUuid },
      list: { callList, upsertCall, removeCall },
      conference: {
        conferencedCalls,
        startTime: conferenceStartTime,
        isMuted: conferenceIsMuted,
        isOnHold: conferenceIsOnHold,
        addCalls: addCallsToConference,
        end: endConference,
        setMute: setConferenceMute,
        setHold: setConferenceHold,
      },
      invite: {
        pendingInvitation,
        pendingInvitationUuid,
        setPendingInvitationUuid,
      },
    },
    trackedParkingLots,
    trackedExtensions,
    pbxExtensionsList,
    audio: {
      output: {
        devices: outputs,
        preference: [selectedOutputDeviceId, setSelectectedOutputDeviceId],
      },
      input: {
        devices: inputs,
        preference: [selectedInputDeviceId, setSelectedInputDeviceId],
      },
    },
    speedDial,
  } = useWebphoneState({
    pbxId: pbxId ?? 0,
    registrationState,
    transportConnectionState,
  })

  const { upsertRecentCall } = useRecentCalls(pbxId, pbxExtensionsList ?? [])

  const activeCallCount = callList.size - (pendingInvitation ? 1 : 0)
  const isAtMaxCallLimit = activeCallCount >= MAX_CALLS

  // #region SIP event handlers
  const handleOnCallCallbacks = useCallback(
    (stateChange: SessionStateChange) => {
      switch (stateChange.state) {
        case SessionState.Establishing:
          break
        case SessionState.Established:
          onCallStart?.()
          break
        case SessionState.Terminating:
          break
        case SessionState.Terminated:
          onCallEnd?.()
          if (onFinalCallEnd) {
            setNeedsFinalCallEnd(true)
          }
          break
        default:
          break
      }
    },
    [onCallStart, onCallEnd, onFinalCallEnd],
  )

  useEffect(() => {
    if (!onCallStart && !onCallEnd && !onFinalCallEnd) return
    if (!sipClient) return
    logger?.debug('Phone endpoint listener registration')
    sipClient.on('OutboundSessionStateChange', handleOnCallCallbacks)
    sipClient.on('InboundSessionStateChange', handleOnCallCallbacks)

    return () => {
      sipClient.off('OutboundSessionStateChange', handleOnCallCallbacks)
      sipClient.off('InboundSessionStateChange', handleOnCallCallbacks)
    }
  }, [sipClient])
  // #endregion

  // #region Actions
  const holdCallInternal = async (call: PhoneCall, hold: boolean) => {
    if (!sipClient) return
    try {
      await sipClient.holdCall(call.callUuid, hold)
      upsertCall({ ...call, isOnHold: hold })
    } catch (e: unknown) {
      // We only see failures here when users jam on the hold button,
      // the SIP client handles the actual hold as expected.
      logger?.info('Error while holding call')
    }
  }

  const holdCall = async (callUuid: string, hold?: boolean) => {
    const call = callList.get(callUuid)
    if (!call || call.isOnHold === hold) return
    await holdCallInternal(call, hold === undefined ? !call.isOnHold : hold)
  }

  const toggleHoldConference = async () => {
    if (!sipClient) return
    if (!conferencedCalls) return
    const holdStatus = !conferenceIsOnHold
    await Promise.all(
      conferencedCalls.map((call) => holdCallInternal(call, holdStatus)),
    )
    setConferenceHold(holdStatus)
  }

  const startCall = (
    destination:
      | string
      | { contactNumber: ContactNumber; contact: ContactDetails },
    outboundCallerIdName?: string,
  ) => {
    if (!sipClient) return
    if (activeCall) {
      holdCallInternal(activeCall, true)
    }
    let targetNumber = ''
    let displayNameOverride: string | undefined
    if (typeof destination === 'string') {
      targetNumber = destination
    } else {
      targetNumber = destination.contactNumber.number
      displayNameOverride = destination.contact.name
    }
    if (targetNumber.length <= 5) {
      if (
        targetNumber.length === 4 &&
        targetNumber.startsWith(PARKING_LOT_PREFIX_DIGITS)
      ) {
        const parkingLot = trackedParkingLots.list.find(
          (lot) => lot.targetNumber === targetNumber,
        )
        displayNameOverride = parkingLot?.label
      }
      const extension = pbxExtensionsList.find(
        (ext) => ext.number === targetNumber,
      )
      if (extension) {
        displayNameOverride = extension.name
      }
    }
    const newCallUuid = crypto.randomUUID()
    sipClient.startCall(
      newCallUuid,
      targetNumber,
      displayNameOverride,
      outboundCallerIdName,
    )
    upsertCall({
      status: PhoneCallStatus.INITIAL,
      callUuid: newCallUuid,
      number: targetNumber,
      name: displayNameOverride ?? null,
      startTime: null,
      isOnHold: false,
      isMuted: false,
    })
    setActiveCallUuid(newCallUuid)
    setNeedsClientAudioInput(true)
    upsertRecentCall(destination)
  }
  const endCall = (callUuid: string) => {
    logger?.info('End call', { callUuid })
    if (!sipClient) return
    try {
      sipClient.endCall(callUuid)
    } catch (e) {
      logger?.info('Error ending call', { callUuid, error: e })
    }
  }

  const muteCallInternal = (call: PhoneCall, mute: boolean) => {
    if (!sipClient) return
    sipClient.muteCall(call.callUuid, mute)
    upsertCall({ ...call, isMuted: mute })
  }

  const muteCall = (
    callUuid: string,
    muted: boolean | undefined = undefined,
  ) => {
    const call = callList.get(callUuid)
    if (!call || call.isMuted === muted) return
    muteCallInternal(call, muted === undefined ? !call.isMuted : muted)
  }

  const toggleMuteConference = () => {
    if (!sipClient) return
    setConferenceMute(!conferenceIsMuted)
    setNeedsAudioConnected(true)
  }

  const attendedTransferCall = (callUuid: string, targetCallUuid: string) => {
    if (!sipClient) return
    const call = callList.get(callUuid)
    if (!call) return
    const targetCall = callList.get(targetCallUuid)
    if (!targetCall || targetCall.status !== PhoneCallStatus.CONNECTED) return
    if (call.status !== PhoneCallStatus.CONNECTED) return
    sipClient.attendedTransferCall(callUuid, targetCallUuid)
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.WEBPHONE,
      operation: WebphoneOperation.CallTransferred,
      context: {
        callName: call.name || toMaskedPhoneNumber(call.number),
        transferToName:
          targetCall.name || toMaskedPhoneNumber(targetCall.number),
      },
    })
    track(WebPhoneAnalyticsEvents.WarmTransfer)
  }

  const blindTransferCall = (callUuid: string, target: string) => {
    if (!sipClient) return
    const call = callList.get(callUuid)
    if (!call) return
    if (call.status !== PhoneCallStatus.CONNECTED) return
    sipClient.blindTransferCall(callUuid, target)
    if (target.startsWith(PARKING_LOT_PREFIX_STRING)) {
      const parkTarget = target.split('+')[1]
      const parkingLot = trackedParkingLots.list.find(
        (lot) => lot.targetNumber === parkTarget,
      )
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Success,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallParked,
        context: {
          callName: call.name || toMaskedPhoneNumber(call.number),
          parkingLotName: parkingLot?.label ?? parkTarget,
        },
      })
      return
    }

    const extension = pbxExtensionsList.find((ext) => ext.number === target)

    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.WEBPHONE,
      operation: WebphoneOperation.CallTransferred,
      context: {
        callName: call.name || toMaskedPhoneNumber(call.number),
        transferToName: extension?.name ?? toMaskedPhoneNumber(target),
      },
    })
    track(WebPhoneAnalyticsEvents.ColdTransfer)
  }

  const parkCall = (callUuid: string, parkTarget: string) => {
    blindTransferCall(callUuid, `${PARKING_LOT_PREFIX_STRING}${parkTarget}`)
  }

  const unparkCall = (parkTarget: string) => {
    if (!sipClient) return
    startCall(parkTarget)
  }

  const answerCall = () => {
    if (!sipClient || !pendingInvitationUuid) return

    logger?.info('Answer call', { callUuid: pendingInvitationUuid })
    sipClient.answerCall(pendingInvitationUuid)

    setActiveCallUuid(pendingInvitationUuid)
    setPendingInvitationUuid(null)
    setNeedsClientAudioInput(true)
    setNeedsAudioConnected(true)

    track(WebPhoneAnalyticsEvents.InviteAnswered)
  }

  const answerCallWithHoldCurrent = async () => {
    if (activeCall) {
      await holdCallInternal(activeCall, true)
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Success,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallHeld,
        context: activeCall.name || toMaskedPhoneNumber(activeCall.number),
      })
    }
    answerCall()
  }

  const answerCallWithEndCurrent = () => {
    if (activeCall) {
      endCall(activeCall.callUuid)
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallEnded,
        context: activeCall.name || toMaskedPhoneNumber(activeCall.number),
      })
    }
    answerCall()
  }

  const answerCallWithParkCurrent = (parkId: string) => {
    if (activeCall) {
      parkCall(activeCall.callUuid, parkId)
    }
    answerCall()
  }

  const rejectCall = () => {
    if (!sipClient || !pendingInvitationUuid) return

    logger?.info('Reject call', { callUuid: pendingInvitationUuid })
    sipClient.rejectCall(pendingInvitationUuid)
    setPendingInvitationUuid(null)
    removeCall(pendingInvitationUuid)

    if (pendingInvitation) {
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Error,
        domain: NotificationDomain.WEBPHONE,
        operation: WebphoneOperation.CallDeclined,
        context:
          pendingInvitation.name ||
          toMaskedPhoneNumber(pendingInvitation.number),
      })
    }
  }

  const rejectCallWithBusy = () => {
    if (!sipClient || !pendingInvitationUuid) return

    logger?.info('Reject call with busy', { callUuid: pendingInvitationUuid })
    sipClient.rejectWithBusy(pendingInvitationUuid)
    setPendingInvitationUuid(null)
    removeCall(pendingInvitationUuid)
  }

  const switchCall = async (callUuid: string) => {
    logger?.info('Switch call', { callUuid })
    if (activeCall) {
      await holdCallInternal(activeCall, true)
    }

    const call = callList.get(callUuid)
    if (!call) return
    await holdCallInternal(call, false)
    setActiveCallUuid(callUuid)
    setNeedsAudioConnected(true)
  }

  const mergeCalls = async (callUuids: string[]) => {
    addCallsToConference(callUuids)
    await callList.forEach(async (call) => {
      if (callUuids.includes(call.callUuid)) {
        muteCallInternal(call, false)
        if (call.isOnHold) {
          await holdCallInternal(call, false)
        }
      }
    })
    setNeedsAudioConnected(true)
  }

  const splitCalls = async () => {
    endConference()
    await callList.forEach(async (call) => {
      if (call.callUuid !== activeCall?.callUuid && !call.isOnHold) {
        await holdCallInternal(call, true)
      }
    })
    if (!activeCall && callList.size > 0) {
      const firstCall = callList.keys().next().value
      if (isPhoneCall(firstCall)) {
        setActiveCallUuid(firstCall.callUuid)
        await holdCallInternal(firstCall, false)
      }
    }
    setNeedsAudioConnected(true)
  }

  const endConferenceCall = () => {
    conferencedCalls?.forEach((call) => {
      endCall(call.callUuid)
    })
    endConference()
  }

  const sendDtmf = (callUuid: string, digit: string) => {
    if (!sipClient) return
    const call = callList.get(callUuid)
    if (!call) return
    if (call.status !== PhoneCallStatus.CONNECTED) return
    sipClient.sendDtmf(callUuid, digit)
  }

  const resetConnection = () => {
    setConnectionRefreshRequested(true)
  }

  const switchAudioOutput = (deviceId: string) => {
    logger?.info('Switch audio output', { deviceId })
    setSelectectedOutputDeviceId(deviceId)
  }

  const switchAudioInput = (deviceId: string) => {
    logger?.info('Switch audio input', { deviceId })
    setSelectedInputDeviceId(deviceId)
  }

  if (needsFinalCallEnd) {
    setNeedsFinalCallEnd(false)
    if (callList.size === 0) {
      onFinalCallEnd?.()
    }
  }

  const conferenceReturn =
    conferencedCalls && conferenceStartTime
      ? { conferencedCalls, startTime: conferenceStartTime }
      : { conferencedCalls: undefined, startTime: undefined }

  return {
    state: {
      phoneStatus,
      phoneCalls: {
        active: { activeCall },
        list: { callList },
        conference: {
          ...conferenceReturn,
          isMuted: conferenceIsMuted,
          isOnHold: conferenceIsOnHold,
        },
        invite: {
          pendingInvitation,
          pendingInvitationUuid,
        },
        isAtMaxCallLimit,
      },
      extensions: pbxExtensionsList,
      trackedExtensions: trackedExtensions.withPresence,
      trackedParkingLots: trackedParkingLots.withPresence,
      registrationError,
      audioDevices: {
        output: {
          devices: outputs,
          preference: selectedOutputDeviceId,
        },
        input: {
          devices: inputs,
          preference: selectedInputDeviceId,
        },
      },
      speedDial,
    },
    actions: {
      switchCall,
      startCall,
      endCall,
      answerCall,
      answerCallWithHoldCurrent,
      answerCallWithEndCurrent,
      answerCallWithParkCurrent,
      rejectCall,
      rejectCallWithBusy,
      muteCall,
      holdCall,
      attendedTransferCall,
      transferCall: blindTransferCall,
      parkCall,
      unparkCall,
      mergeCalls,
      splitCalls,
      endConferenceCall,
      toggleMuteConference,
      toggleHoldConference,
      sendDtmf,
      resetConnection,
      switchAudioOutput,
      switchAudioInput,
    },
  }
}
